import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logo from "../../../assets/logos/logoX.png";
import arrow from "../../../assets/icons/arrow-right-blue.svg";
import download from "../../../assets/icons/download-white.svg";
import { useLocation } from "react-router-dom";
import {
  getFile,
  getFileData,
  isValidToken,
} from "../../../services/Api/workingFolderService";
import {
  showToastError,
  showToastSuccess,
} from "../../../services/Toasts/toasts";
import { useLoaderFile } from "../../../stores/Utils/loaderFile";
import LoadingUploadFile from "../../../components/Cac/Partner/Utils/LoadingUploadFile";

const CustomerWorkingFolder = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [open, setOpen] = useState(false);
  const [validToken, setValidToken] = useState(false);
  const [data, setData] = useState<any>();
  const [dataName, setDataName] = useState<any>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const { setPercent, isLoading, setIsLoading } = useLoaderFile();
  const [block, setBlock] = useState<boolean>(false);
  const [downloadOK, setDownloadOK] = useState<boolean>(false);

  useEffect(() => {
    if (location.pathname.includes("workingfolder")) {
      setBlock(false);
      setDownloadOK(false);
    }
  }, [location]);

  useEffect(() => {
    if (token) {
      isValidToken(token)
        .then((res) => {
          setDataName(res.data);
          setValidToken(true);
        })
        .catch(() => setValidToken(false));
    }
  }, []);

  const handlePassword = () => {
    getFileData(String(token), password)
      .then((data) => {
        setData(data);
        setOpen(true);
      })
      .catch(() => {
        showToastError(t("workingFolder.wrongPassword"));
        setOpen(false);
      });
  };

  const handleDownload = async () => {
    setIsLoading(true);
    setDownloadOK(false);
    setPercent(0);
    try {
      await getFile(String(token), password, data.fileName, (progressEvent) => {
        if (progressEvent && progressEvent.loaded && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setPercent(percentCompleted);
        }
      });
      setIsLoading(false);
      setBlock(true);
      setDownloadOK(true);
      setPercent(0);
      showToastSuccess(t("workingFolder.downloadSuccess"));
    } catch (error) {
      showToastError(t("workingFolder.downloadError"));
      setIsLoading(false);
      setPercent(0);
    }
  };

  return (
    <div className="w-screen h-screen flex relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100"></div>
      
      {/* Logo */}
      <div className="hidden md:block absolute top-6 left-8 z-30 transition-transform duration-300 hover:scale-110">
        <img src={logo} alt="logo Nexum" className="w-12 h-16 drop-shadow-lg" />
      </div>
      
      {/* Main container */}
      <div className="w-full h-screen flex items-center justify-center flex-col relative z-20">
        <div className="3xl:w-2/12 md:w-4/12 w-6/12 flex flex-col items-center justify-around bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl relative z-20 border border-white/20 overflow-hidden transition-all duration-500 hover:shadow-3xl hover:scale-[1.02]">
          
          {/* Decorative elements */}
          <div className="w-20 h-20 border-2 border-dark-lavender rounded-2xl bg-gradient-to-br from-light-lavender to-dark-lavender2 absolute -top-10 -left-10 z-10 shadow-2xl rotate-12 transition-transform duration-300"></div>
          
          {/* Header */}
          <div className="text-center pt-8 pb-4">
            <h2 className="text-gray-700 font-bold 3xl:text-4xl text-2xl font-K2D mb-2 tracking-wide">
              {t("workingFolder.title")}
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-yellow-trio to-yellow-400 rounded-full mx-auto"></div>
          </div>
          
          {/* Content */}
          <div className="w-10/12 space-y-6">
            {validToken && !open && (
              <>
                {/* Welcome message */}
                <div className="text-center space-y-4">
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <h3 className="text-xl font-bold text-gray-700 font-K2D mb-2">
                      {t("workingFolder.welcome")}
                    </h3>
                    <p className="text-gray-600 font-BarlowCondensed text-sm leading-relaxed">
                      <span className="font-semibold text-gray-700">
                        {dataName?.nameManager} – {dataName?.namePartner}
                      </span>
                      <br />
                      {t("workingFolder.wantsToShare")}
                    </p>
                    <div className="mt-3 p-3 bg-yellow-trio/20 rounded-lg border border-yellow-trio/30">
                      <p className="font-bold text-lg text-gray-700">
                        {dataName?.nameCategoryMission}-{dataName?.exercice}
                      </p>
                    </div>
                    <p className="text-gray-600 text-sm mt-3">
                      {t("workingFolder.securityHint")}
                    </p>
                  </div>
                </div>

                {/* Password field */}
                <div className="space-y-2">
                  <label
                    htmlFor="password"
                    className="text-gray-700 text-sm 3xl:text-lg font-BarlowCondensed font-medium block"
                  >
                    {t("workingFolder.password")}
                  </label>
                  <div className="relative group">
                    <input
                      type="password"
                      className="w-full h-12 px-4 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-yellow-trio focus:bg-white focus:outline-none transition-all duration-300 shadow-sm group-hover:shadow-md text-gray-700 placeholder-gray-400 form-input-modern"
                      pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                      onChange={(e) => setPassword(e.target.value)}
                      minLength={10}
                      maxLength={200}
                      value={password}
                      name="password"
                      id="password"
                      placeholder="••••••••"
                      required
                    />
                    <div className="absolute inset-0 rounded-xl transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-yellow-trio/10 group-hover:to-yellow-400/10 pointer-events-none"></div>
                  </div>
                </div>

                {/* Validate button */}
                <button
                  className="w-full font-BarlowCondensed cursor-pointer text-lg h-12 bg-gradient-to-r from-yellow-trio to-yellow-400 text-gray-700 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 border-0 focus:outline-none focus:ring-2 focus:ring-yellow-trio focus:ring-offset-2 btn-modern"
                  onClick={handlePassword}
                >
                  {t("workingFolder.validate")}
                </button>

                {/* Arrow decoration */}
                <div className="flex justify-center mb-4">
                  <img
                    src={arrow}
                    alt="fleche"
                    className="w-5 h-5 rotate-90 opacity-60"
                  />
                </div>
              </>
            )}
            
            {/* Error message */}
            {(!validToken && !open) || (block && !downloadOK) ? (
              <div className="text-center p-6 bg-red-50 border border-red-200 rounded-xl mb-4">
                <div className="p-3 bg-red-100 rounded-full w-fit mx-auto mb-3">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <p className="text-red-700 font-medium text-lg">
                  {t("workingFolder.expiredTitle")}
                </p>
                <p className="text-red-600 text-sm mt-2">
                  {t("workingFolder.expiredText")}
                </p>
              </div>
            ) : null}

            {/* Success message */}
            {downloadOK ? (
              <div className="text-center p-6 bg-green-100 border border-green-200 rounded-xl mb-4">
                 <div className="p-3 bg-green-100 rounded-full w-fit mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                <p className="text-green-700 font-medium text-lg">
                  {t("workingFolder.downloadSuccess")}
                </p>
              </div>
            ) : null}

            {/* Success message */}
            {open && !block && (
              <div className="space-y-4">
                <div className="text-center p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="p-3 bg-green-100 rounded-full w-fit mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-green-700 font-medium text-lg">
                    {t("workingFolder.accessGranted")}
                  </p>
                  <p className="text-green-600 text-sm mt-1">
                    {t("workingFolder.folderOf", { name: dataName?.nameManager })}
                  </p>
                </div>

                {/* Download section */}
                <div className="bg-white border-2 border-gray-100 rounded-xl p-6 shadow-s mb-4">
                  {isLoading ? (
                    <div className="flex flex-col items-center space-y-4">
                      <LoadingUploadFile />
                      <p className="text-gray-600 text-sm">{t("workingFolder.downloading")}</p>
                    </div>
                  ) : (
                    <button
                      onClick={handleDownload}
                      className="w-full h-12 bg-gradient-to-r from-yellow-trio cursor-pointer to-yellow-400 text-gray-700 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3 group"
                    >
                      <img src={download} alt="download" className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
                      <span>{t("workingFolder.downloadFolder")}</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Decorative element */}
          <div className="w-20 h-20 border-2 border-light-lavender rounded-2xl bg-gradient-to-br from-light-lavender to-dark-lavender absolute -bottom-10 -right-10 z-10 shadow-2xl -rotate-12 transition-transform duration-300"></div>
        </div>
      </div>
    </div>
  );
};

export default CustomerWorkingFolder;
